<template>
  <div
    v-if="aiEnabled"
    class="agent-toggle"
    :class="{ active: active }"
    data-agent="agent-toggle"
  >
    <span
      class="agent-toggle-ws"
      :title="active ? $t('agent.toggle.disable') : $t('agent.toggle.enable')"
      @click.stop="toggle"
    >
      <span class="agent-toggle-robot">🤖</span>
      <span class="agent-toggle-dot" :class="{ connected: active }"></span>
    </span>
    <span
      class="agent-toggle-chat"
      :class="{ 'chat-open': chatOpen }"
      :title="$t('agent.assistant')"
      @click.stop="toggleChat"
    >
      <i class="fp4 fp4-message-lines"></i>
    </span>
  </div>
</template>

<script>
import { resolveFeatureFlag } from '@/shared/useFeatureFlag'
import { getAgentSession } from './agentSession'
import { getSessionId } from './useSessionId'
import Config from '@/shared/Config'

export default {
  data () {
    return {
      featureEnabled: false,
      isProjectContext: false,
      active: false,
      chatOpen: false
    }
  },
  computed: {
    aiEnabled () {
      return this.featureEnabled && this.isProjectContext
    }
  },
  async mounted () {
    this.featureEnabled = await resolveFeatureFlag('ai_agent')
    const config = await Config()
    this.isProjectContext = !!config.PROJECT_ID

    if (!this.aiEnabled) return

    const session = getAgentSession()
    const sessionId = await getSessionId()

    session.onChange(({ active, chatOpen }) => {
      this.active = active
      if (chatOpen !== undefined) this.chatOpen = chatOpen
    })

    // Try to recover from a previous session (page reload)
    const recovered = await session.tryRecover(sessionId)
    if (recovered) {
      this.active = true
    }

    // Hook into Vue router to notify gateway when user navigates
    if (this.$router) {
      this.$router.afterEach(() => {
        if (session.isActive()) {
          session.notifyRouteChanged()
        }
      })
    }
  },
  methods: {
    async toggle () {
      const session = getAgentSession()
      const sessionId = await getSessionId()
      await session.toggle(sessionId)
    },
    async toggleChat () {
      const session = getAgentSession()
      // If WS not active, activate it first
      if (!session.isActive()) {
        const sessionId = await getSessionId()
        await session.activate(sessionId)
      }
      session.requestOpenChat()
    }
  }
}
</script>

<style lang="less" scoped>
.agent-toggle {
  display: flex;
  align-items: center;
  height: 100%;
  padding: 0 4px;
}

.agent-toggle-ws {
  display: flex;
  align-items: center;
  position: relative;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 4px;
  opacity: 0.5;
  transition: opacity 0.2s, background 0.2s;

  &:hover {
    opacity: 0.8;
    background: rgba(255, 255, 255, 0.05);
  }

  .active & {
    opacity: 1;
  }
}

.agent-toggle-robot {
  font-size: 18px;
  line-height: 1;
  filter: grayscale(1);
  transition: filter 0.2s;

  .active & {
    filter: grayscale(0);
  }
}

.agent-toggle-chat {
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  margin-left: 4px;
  color: @grey;
  transition: color 0.2s;

  i {
    font-size: 20px;
    line-height: 1;
  }

  &:hover {
    color: @blue;
  }

  &.chat-open {
    color: @blue;
  }
}

.agent-toggle-dot {
  position: absolute;
  top: 2px;
  right: 2px;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: @grey;
  transition: background 0.2s;

  &.connected {
    background: @green;
  }
}
</style>
